import { computed, effect, inject, Injectable, Service, signal } from '@angular/core';
import {
  ConsultantOrdreSignatureEnum,
  ConsultantPreferencesInput,
  ConsultantPreferencesSettingsGQL,
  GenerationRecueilTypeEnum,
  PreferencesSettingsFragmentFragment,
  UpdateConsultantPreferencesSettingsGQL,
} from '@lib/models/generated/graphql';
import { AuthService } from '@lib/services/auth-service.service';
import { QueryManagerService } from 'finzzle-angular-utils';

const backOfficePreferencesSettings: PreferencesSettingsFragmentFragment = {
  ordreSignature: ConsultantOrdreSignatureEnum.ConsultantEnPremier,
  generationRecueilType: GenerationRecueilTypeEnum.Couple,
  recueilChampsComplementairesVisible: false,
};
@Injectable({ providedIn: 'root' })
export class ConsultantPreferencesService {
  private authService = inject(AuthService);
  private queryManagerService = inject(QueryManagerService);
  private readonly preferencesSettingsGQL = inject(ConsultantPreferencesSettingsGQL);
  private readonly updatePreferencesSettingsGQL = inject(UpdateConsultantPreferencesSettingsGQL);

  private readonly preferencesSettings = signal<PreferencesSettingsFragmentFragment | undefined>(undefined);
  private readonly _ordreSignature = signal(ConsultantOrdreSignatureEnum.ConsultantEnPremier);
  private readonly _generationRecueilType = signal(GenerationRecueilTypeEnum.Couple);
  private readonly _recueilChampsComplementairesVisible = signal<boolean>(false);

  public readonly ordreSignature = this._ordreSignature.asReadonly();
  public readonly generationRecueilType = this._generationRecueilType.asReadonly();
  public readonly recueilChampsComplementairesVisible = this._recueilChampsComplementairesVisible.asReadonly();
  public readonly preferencesLoaded = computed(() => this.preferencesSettings() !== undefined);

  public isUpdating = signal(false);
  constructor() {
    this.setupPreferencesWatcher();
  }

  private setupPreferencesWatcher(): void {
    effect(async () => {
      // wait for the access right check
      if (!this.authService.accessRightChecked()) {
        return;
      }
      const canAccessApp = this.authService.canAccessApp();
      if (canAccessApp && !this.authService.isBackOffice()) {
        await this.getPreferencesSettings();
      } else if (canAccessApp && this.authService.isBackOffice()) {
        this.setPreferencesValuesFromSettings(backOfficePreferencesSettings);
      }
    });
  }

  async getPreferencesSettings() {
    try {
      const result = await this.queryManagerService.handleQueryErrorsAsync(
        this.preferencesSettingsGQL.fetch({ fetchPolicy: 'network-only' })
      );
      const settings = result.data?.consultantPreferences[0];
      this.setPreferencesValuesFromSettings(settings);
      return settings;
    } catch (error) {
      console.error('Erreur lors de la récupération des préférences:', error);
      throw error;
    }
  }

  private async updatePreferencesSettings(input: ConsultantPreferencesInput) {
    this.isUpdating.set(true);
    try {
      const result = await this.queryManagerService.handleMutationErrorsAsync(
        this.updatePreferencesSettingsGQL.mutate({ variables: { input } })
      );
      const updatedSettings = result.data?.createOrUpdateConsultantPreferences[0];
      this.setPreferencesValuesFromSettings(updatedSettings);
    } catch (error) {
      console.error('Erreur lors de la mise à jour des préférences:', error);
      throw error;
    } finally {
      this.isUpdating.set(false);
    }
  }

  public async setOrdreSignature(ordre: ConsultantOrdreSignatureEnum | null) {
    this._ordreSignature.set(ordre ?? ConsultantOrdreSignatureEnum.ConsultantEnPremier);
    await this.updatePreferencesSettings({ ordreSignature: ordre ?? ConsultantOrdreSignatureEnum.ConsultantEnPremier });
  }
  public async setGenerationRecueilType(type: GenerationRecueilTypeEnum | null) {
    this._generationRecueilType.set(type ?? GenerationRecueilTypeEnum.Couple);
    await this.updatePreferencesSettings({ generationRecueilType: type ?? GenerationRecueilTypeEnum.Couple });
  }

  public async setRecueilChampsComplementairesVisible(visible: boolean | null) {
    this._recueilChampsComplementairesVisible.set(visible ?? false);
    await this.updatePreferencesSettings({ recueilChampsComplementairesVisible: visible ?? false });
  }

  setPreferencesValuesFromSettings(settings: PreferencesSettingsFragmentFragment | undefined) {
    if (!settings) return;
    this.preferencesSettings.set(settings);
    this._ordreSignature.set(settings.ordreSignature ?? ConsultantOrdreSignatureEnum.ConsultantEnPremier);
    this._generationRecueilType.set(settings.generationRecueilType ?? GenerationRecueilTypeEnum.Couple);
    this._recueilChampsComplementairesVisible.set(settings.recueilChampsComplementairesVisible ?? false);
  }
}
